import { useCallback } from "react"
import { useSelector } from "react-redux"
import axios, { AxiosResponse } from "axios"

// Selectors
import { selectCurrentToken } from "Containers/Login/selectors"

// Utils
import { config } from "Utils/config"

/**
 * Hook personnalisé pour récupérer les données brutes d'un ou plusieurs fichiers (par exemple JSON, TXT).
 * Il ne déclenche pas de téléchargement, mais retourne le contenu des fichiers dans un tableau.
 */
export function useFetchMultipleFilesData<T = unknown>() {
  /** Selectors */
  const currentToken = useSelector(selectCurrentToken)

  return useCallback(
    /**
     * @param idFiles Un tableau d'identifiants de fichiers à récupérer.
     * @returns Une promesse qui se résout en un tableau des données des fichiers (type T[]),
     * ou null en cas d'erreur ou si le token n'est pas disponible.
     */
    async (idFiles: string[]): Promise<T[] | null> => {
      if (!currentToken) {
        console.error("☠️ Token d'authentification non disponible.")
        return null
      }

      if (idFiles.length === 0) {
        return []
      }

      try {
        const requests = idFiles.map(idFile =>
          axios.get<T>(`${config.APOLLO_SERVER_URL}/file/read/${idFile}?preview=1`, {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          })
        )

        const responses: AxiosResponse<T>[] = await Promise.all(requests)
        const allData: T[] = responses.map(response => response.data)

        return allData
      } catch (error) {
        console.error(
          "☠️ Erreur lors de la récupération des données d'un ou plusieurs fichiers :",
          error
        )

        return null
      }
    },
    [currentToken]
  )
}
